import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import { SrOrderStatus, SrSalesOrderType } from '@plato/data/starrocks/types';
import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { toCurrencyString, toLocaleString } from '@plato/ui-legacy/lib/currency-helpers';
import { formatShortDate } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import {
    Card,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardMainAction,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { Skeleton } from '@plato/ui/primitives/skeleton';
import type { Namespace, TFunction } from 'i18next';
import { CircleAlertIcon, ClipboardListIcon, PackageIcon, UserIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { formatOrderId } from '../../../lib/order-helpers.ts';
import { ActivityFeedModelType } from '../../../server/types/customer-activity-feed-types.ts';
import type { QuoteOrderPayload } from '../../../server/types/customer-activity.ts';
import type { ExtendedSalesOrderItem } from '../../../server/types/grid-expanded-types.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import { useDetailQuoteOrder } from '../../quote-order-detail/hooks/use-detail-quote-order.tsx';

/** The first positions are shown in full; the rest collapse into one summed row. */
const visiblePositionLimit = 3;

const loadingNameWidths = ['w-3/5', 'w-4/5', 'w-1/2', 'w-2/3'];

/**
 * The state's tone: settled reads success, still moving reads warning, lost or cancelled
 * reads danger, and anything in between stays grey.
 *
 * One map covers both document types because their statuses barely overlap — a quote is
 * OPEN / RECEIVED / LOST / PARTIALLY_RECEIVED, an order takes the rest, and the shared
 * OPEN means the same waiting either way. `satisfies` makes a new status fail to compile
 * until it is given a tone here.
 */
const stateToneClasses: Record<string, string> = {
    RECEIVED: 'text-success',
    BILLED: 'text-success',
    PROCESSED_INTERNALLY: 'text-success',
    COMPLETED: 'text-success',
    LOST: 'text-danger',
    CANCELLED: 'text-danger',
    RETURNED: 'text-danger',
    OPEN: 'text-warning',
    PENDING: 'text-warning',
    OPEN_IN_PICKING: 'text-warning',
    PARTIALLY_PICKED: 'text-warning',
    PICKED: 'text-warning',
    RELEASED_FOR_DELIVERY: 'text-warning',
    PARTIALLY_BILLED: 'text-content-weaker',
    PARTIALLY_RECEIVED: 'text-content-weaker',
    PARTIALLY_SHIPPED: 'text-content-weaker',
    SHIPPED: 'text-content-weaker',
    REVISED: 'text-content-weaker',
    UNKNOWN: 'text-content-weaker',
    OTHER: 'text-content-weaker',
} satisfies Record<SrOrderStatus, string>;

/**
 * The status' own label. Spelled out rather than `t(status)` so every key exists as a
 * literal in the source and stays greppable against `order-status.json`.
 */
function getOrderStatusLabel(status: string, t: TFunction<Namespace>): string {
    switch (status) {
        case SrOrderStatus.OPEN:
            return t('OPEN', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.PENDING:
            return t('PENDING', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.OPEN_IN_PICKING:
            return t('OPEN_IN_PICKING', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.PARTIALLY_PICKED:
            return t('PARTIALLY_PICKED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.PICKED:
            return t('PICKED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.RELEASED_FOR_DELIVERY:
            return t('RELEASED_FOR_DELIVERY', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.PARTIALLY_SHIPPED:
            return t('PARTIALLY_SHIPPED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.SHIPPED:
            return t('SHIPPED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.PARTIALLY_BILLED:
            return t('PARTIALLY_BILLED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.BILLED:
            return t('BILLED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.PROCESSED_INTERNALLY:
            return t('PROCESSED_INTERNALLY', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.COMPLETED:
            return t('COMPLETED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.PARTIALLY_RECEIVED:
            return t('PARTIALLY_RECEIVED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.RECEIVED:
            return t('RECEIVED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.REVISED:
            return t('REVISED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.RETURNED:
            return t('RETURNED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.CANCELLED:
            return t('CANCELLED', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.LOST:
            return t('LOST', { ns: 'order-status', defaultValue: status });
        case SrOrderStatus.OTHER:
            return t('OTHER', { ns: 'order-status', defaultValue: status });
        default:
            return t('UNKNOWN', { ns: 'order-status', defaultValue: status });
    }
}

/** The fields the card reads, so a visit's linked quotes can render it too. */
export type ActivityFeedQuoteOrderItemPayload = Pick<
    QuoteOrderPayload,
    | 'sales_order_id'
    | 'display_id'
    | 'status'
    | 'total_amount_net'
    | 'total_margin'
    | 'line_items_count'
    | 'po_number'
    | 'entry_date'
    | 'clerk_name'
    | 'customer_id'
    | 'customer_name'
>;

export type ActivityFeedQuoteOrderItemProps = {
    payload: ActivityFeedQuoteOrderItemPayload;
    modelType: typeof ActivityFeedModelType.QUOTE | typeof ActivityFeedModelType.ORDER;
};

/**
 * One quote or order with its first positions. Opens the document.
 *
 * The type is carried by the title rather than the icon: a tenant with
 * `stripOrderIdPrefix` loses the `A-` / `20-` that would otherwise tell them apart.
 * Positions are not in the feed payload, so they load once the card is in view.
 */
export function ActivityFeedQuoteOrderItem({
    payload,
    modelType,
}: ActivityFeedQuoteOrderItemProps): ReactElement {
    const { t } = useTranslation('activity-feed');
    const locale = useResolvedLocale();
    const appConfig = useAppConfig();
    const { openOrderQuoteDetails } = useDialogs();
    const { ref: inViewRef, inView } = useInView({ triggerOnce: true });

    const isQuote = modelType === ActivityFeedModelType.QUOTE;
    const type = isQuote ? SrSalesOrderType.QUOTE : SrSalesOrderType.ORDER;
    const displayId = formatOrderId(appConfig, payload.display_id || payload.sales_order_id);
    const title = isQuote
        ? t('card__quote_title', { id: displayId })
        : t('card__order_title', { id: displayId });
    const margin = appConfig.canReadGrossMargin ? payload.total_margin : undefined;

    const { data, isPending, isError } = useDetailQuoteOrder({
        transactionId: payload.sales_order_id,
        enabled: inView && payload.line_items_count > 0,
    });

    return (
        <Card ref={inViewRef}>
            <CardFront>
                <CardStart>{isQuote ? <ClipboardListIcon /> : <PackageIcon />}</CardStart>
                <CardContent>
                    <CardHeader>
                        <CardTitle className="flex-1 truncate">{title}</CardTitle>
                        <CardSubtitle className="tabular-nums">
                            <span className={stateToneClasses[payload.status] ?? 'text-content-weaker'}>
                                {getOrderStatusLabel(payload.status, t)}
                            </span>
                            <span>·</span>
                            <span className="text-content">
                                {toCurrencyString(payload.total_amount_net, locale)}
                            </span>
                        </CardSubtitle>
                    </CardHeader>
                    {payload.line_items_count > 0 && isError && (
                        <CardDetail className="flex items-start gap-tight text-body">
                            <CircleAlertIcon className="mt-0.75 size-3.5 shrink-0" />
                            {t('card__positions_load_error')}
                        </CardDetail>
                    )}
                    {payload.line_items_count > 0 && !isError && (
                        <PositionList
                            lineItems={isPending ? undefined : data.lineItems}
                            count={payload.line_items_count}
                        />
                    )}
                    <CardDetail className="flex flex-wrap items-center gap-x-tight gap-y-0.5 text-ui-sm tabular-nums text-content-weaker">
                        {payload.clerk_name && (
                            <span className="flex min-w-0 items-center gap-tight whitespace-nowrap">
                                <UserIcon className="size-3.5 shrink-0" />
                                <span className="truncate">{payload.clerk_name} ·</span>
                            </span>
                        )}
                        {payload.po_number && (
                            <span className="min-w-0 truncate">
                                {t('notes__offers_kommission', { ns: 'notes', number: payload.po_number })} ·
                            </span>
                        )}
                        <span className="whitespace-nowrap">
                            {formatShortDate(payload.entry_date, locale)}
                            {margin !== undefined && ' ·'}
                        </span>
                        {margin !== undefined && (
                            <MaskedContent>
                                <span className="whitespace-nowrap">
                                    {t('notes__offers_margin_label', { ns: 'notes' })}{' '}
                                    <MarginPercentage margin={margin} revenue={payload.total_amount_net} /> (
                                    {toCurrencyString(margin, locale)})
                                </span>
                            </MaskedContent>
                        )}
                    </CardDetail>
                </CardContent>
                <CardMainAction
                    aria-label={t('card__open', { name: title })}
                    onClick={() => {
                        openOrderQuoteDetails(type, payload.sales_order_id);
                    }}
                />
            </CardFront>
        </Card>
    );
}

type PositionListProps = {
    lineItems: ExtendedSalesOrderItem[] | undefined;
    count: number;
};

// ERP order, matching the detail dialog; whatever doesn't fit collapses into one summed row.
function PositionList({ lineItems, count }: PositionListProps): ReactNode {
    const { t } = useTranslation('notes');
    const locale = useResolvedLocale();

    // the feed's count only stands in until the positions are loaded
    const total = lineItems?.length ?? count;
    // a single extra position is shown rather than "1 more"
    const limit = total === visiblePositionLimit + 1 ? total : visiblePositionLimit;
    const visible = lineItems?.slice(0, limit);
    const trimmed = lineItems?.slice(limit);
    const trimmedCount = Math.max(total - limit, 0);
    const trimmedTotal = trimmed?.reduce((total, position) => total + (position.line_total ?? 0), 0);

    return (
        // Subgrid keeps the quantity column as wide as its longest value across all rows.
        <CardDetail className="grid grid-cols-[minmax(2rem,auto)_minmax(0,1fr)_auto] gap-x-container-cozy gap-y-0.5 text-body">
            {visible === undefined &&
                loadingNameWidths.slice(0, Math.min(count, limit)).map((nameWidth) => (
                    <div key={nameWidth} aria-hidden className="col-span-3 grid grid-cols-subgrid">
                        <div className="flex h-5 items-center justify-end">
                            <Skeleton className="w-6 bg-content-weakest/50" />
                        </div>
                        <div className="flex h-5 items-center">
                            <Skeleton className={nameWidth} />
                        </div>
                        <div className="flex h-5 items-center justify-end">
                            <Skeleton className="w-14 bg-content-weakest/50" />
                        </div>
                    </div>
                ))}
            {visible?.map((position) => (
                <div key={position.sales_order_item_id} className="col-span-3 grid grid-cols-subgrid">
                    <span className="whitespace-nowrap text-right tabular-nums">
                        {toLocaleString(position.quantity, locale)}×
                    </span>
                    <span className="truncate text-content-weak">
                        {position.product_name ?? position.sales_order_item_id}
                    </span>
                    <span className="whitespace-nowrap text-right tabular-nums">
                        {toCurrencyString(position.line_total ?? 0, locale)}
                    </span>
                </div>
            ))}
            {trimmedCount > 0 && (
                <div className="col-span-3 grid grid-cols-subgrid">
                    <span />
                    <span className="truncate">
                        {t('notes__offers_positions_more', { count: trimmedCount })}
                    </span>
                    <span className="whitespace-nowrap text-right tabular-nums">
                        {trimmedTotal !== undefined && toCurrencyString(trimmedTotal, locale)}
                    </span>
                </div>
            )}
        </CardDetail>
    );
}
